/**
 * CiteScoreBadge - V3 source trust indicator.
 *
 * Two rendering modes based on available data:
 *
 * 1. Journal sources (both score + impact_factor present):
 *    Numeric box with IF value + tier label (Tier 1/2/3) + "Impact score" footnote.
 *    Figma: node 7520:32170
 *
 * 2. Non-journal sources (score present, no impact_factor):
 *    Falls back to ConfidenceBadge — a simple coloured pill
 *    ("High confidence" / "Moderate confidence" / "Low confidence").
 *    Figma: node 7556:8122
 *
 * Label rationale: docs/design-docs/source-card/impact-score-labels.md
 */

import { cn } from '@heidi/utils';
import { defineMessages, FormattedMessage } from 'react-intl';
import { getCiteScoreTier } from './authorityTiers';
import { ConfidenceBadge } from './ConfidenceBadge';

// Keyed by the tier's legacy i18next labelKey so the shared tier config
// in authorityTiers.ts stays untouched while this component renders intl.
const tierLabelMessages = defineMessages({
  'chat.impactTier.tier1': {
    defaultMessage: 'Tier 1',
    id: 'XCzEeR81QI',
    description: 'Impact score tier label for the most authoritative journal sources',
  },
  'chat.impactTier.tier2': {
    defaultMessage: 'Tier 2',
    id: 'rB1f9w+fBL',
    description: 'Impact score tier label for moderately authoritative journal sources',
  },
  'chat.impactTier.tier3': {
    defaultMessage: 'Tier 3',
    id: 'rDmv6XoZgY',
    description: 'Impact score tier label for the least authoritative journal sources',
  },
});

interface CiteScoreBadgeProps {
  /** Authoritativeness score (0–100) used to determine tier/colour */
  score: number | null | undefined;
  /** The impact score value to display (typically impact_factor) */
  citeScore: number | null | undefined;
  className?: string;
}

export function CiteScoreBadge({ score, citeScore, className }: CiteScoreBadgeProps) {
  const tier = getCiteScoreTier(score);

  if (!tier) return null;

  if (citeScore === undefined || citeScore === null) {
    return <ConfidenceBadge score={score} className={className} />;
  }

  return (
    <div
      data-testid="source-reliability-classification"
      className={cn('flex items-center gap-2', className)}
    >
      <div
        className={cn(
          'flex h-[30px] min-w-[30px] items-center justify-center rounded-md px-1.5',
          tier.scoreBgClass,
          tier.scoreTextClass
        )}
      >
        <span className="text-xs font-medium leading-none">{citeScore.toFixed(1)}</span>
      </div>
      <div className="flex flex-col items-start">
        <span className="text-xs font-medium leading-none text-text-primary">
          <FormattedMessage
            {...tierLabelMessages[tier.labelKey as keyof typeof tierLabelMessages]}
          />
        </span>
        <span className="mt-1 text-[10px] leading-none text-text-secondary">
          <FormattedMessage
            defaultMessage="Impact score"
            id="aAmSlQDDhk"
            description="Footnote under the journal impact factor tier label"
          />
        </span>
      </div>
    </div>
  );
}
